/* De doorverwijzingen van de site (website.redirects, ADR 0024): een oud adres dat naar een nieuw
 * adres wijst. Een rij heeft geen `id`: het oude adres (`from_path`) is de sleutel. Daarom staat dit
 * naast de soorten content in ./website en niet erin.
 *
 * Hoe de site ze gebruikt (site-repo: lib/redirects.ts, lib/content.ts, proxy.ts):
 *   - Alleen als er op een adres geen pagina staat. Een detailpagina zonder rij en elk onbekend pad
 *     roepen redirectOrNotFound aan: staat het pad in de tabel, dan gaat de bezoeker door naar het
 *     doel, en anders krijgt hij "niet gevonden". Een adres met een pagina komt daar nooit.
 *   - Gezocht wordt op het pad zelf, letterlijk. Vóór het zoeken zet de site hoofdletters om naar
 *     kleine letters (alleen bij een pad zonder extensie) en haalt ze een / aan het eind weg.
 *   - 301 en 308 zijn voor de site hetzelfde (blijvend), 302 en 307 ook (tijdelijk).
 *
 * De regels hier:
 *   - Wat de database afdwingt, staat ook hier: het oude adres begint met /, het doel met / of
 *     https://, en de status is 301, 302, 307 of 308.
 *   - Geweigerd wordt wat de site zou breken of nooit zou werken: een doorverwijzing naar zichzelf,
 *     een keten (het doel verwijst zelf door, of een andere doorverwijzing wijst al naar het oude
 *     adres) en een oud adres waarop een gepubliceerde pagina staat.
 *   - Het oude adres van een bestaande rij ligt vast. Te wijzigen zijn het doel, de status en de
 *     notitie, en alleen wat is gewijzigd, wordt geschreven.
 *   - Een foute doorverwijzing is te verwijderen (besluit Stan, 10-10-2026): één rij, op haar oude
 *     adres, na een bevestiging op het scherm. Een fout oud adres bleef anders voor altijd staan, en
 *     een pagina kon niet terug naar een oude slug zolang er een doorverwijzing vanaf dat pad bestond.
 *     Het is de enige plek in Websitecontent waar iets wordt verwijderd.
 *   - Vóór elke schrijfactie leest de datalaag de tabel en de pagina's opnieuw: het formulier kan
 *     open hebben gestaan terwijl iemand anders iets wijzigde.
 *
 * De client komt als argument binnen, zoals in ./website, zodat de toetsen hem kunnen nabootsen. */

import { isAdres } from './instellingen';
import { SOORTEN, type Rij, type WebsiteClient } from './website';

export const TABEL = 'redirects';
const KOLOMMEN = 'from_path, to_path, status, note, created_at';

export const STATUSSEN = [301, 302, 307, 308] as const;
/** Zo leest de site de status (lib/redirects.ts): 301 en 308 blijvend, de rest tijdelijk. */
export const isBlijvend = (status: number): boolean => status === 301 || status === 308;
export const statusNaam = (status: number): string => `${status}: ${isBlijvend(status) ? 'blijvend' : 'tijdelijk'}`;

export interface Doorverwijzing {
  from_path: string;
  to_path: string;
  status: number;
  note: string | null;
  created_at?: string | null;
}

/** Een adres waarop een pagina uit de content staat, gepubliceerd of als concept. */
export interface Pagina {
  pad: string;
  titel: string;
  /** De soort content in het enkelvoud, bv. Nieuwsbericht. */
  soort: string;
  gepubliceerd: boolean;
}

/** Wat er in het formulier staat. */
export interface Concept {
  from_path: string;
  to_path: string;
  status: number | string;
  note: string;
}
export type Wijziging = Omit<Concept, 'from_path'>;

export const LEEG: Concept = { from_path: '', to_path: '', status: 301, note: '' };

/* De vaste pagina's van de site: de mappen onder src/app/(site) in de site-repo, stand 09-10-2026.
 * Op zo'n adres kijkt de site nooit in de doorverwijzingen. De lijst komt uit de code van de site en
 * niet uit de database: hij kan achterlopen. Daarom geeft hij een waarschuwing en geen weigering.
 * De homepage is de uitzondering: die bestaat altijd. */
export const VASTE_PAGINAS = [
  '/', '/branches', '/contact', '/cookie-statement', '/diensten', '/energieoplossingen', '/kennisbank', '/nieuws', '/over-ons',
  '/privacyverklaring', '/projecten', '/tools', '/tools/diesel-vs-elektrisch', '/tools/ere-calculator', '/tools/flex-e-subsidie', '/vacatures',
];

const EIGEN_SITE = /^https:\/\/(www\.)?greenvision\.energy(?=[/?#]|$)/i;
const HEEFT_EXTENSIE = /\.[a-zA-Z0-9]+$/;

/** Het pad zoals de site het opzoekt: zonder extensie in kleine letters (proxy.ts in de site-repo). */
const opzoekPad = (pad: string): string => (HEEFT_EXTENSIE.test(pad) ? pad : pad.toLowerCase());

/** Het pad op de eigen site waar een doel naartoe wijst, zonder ?, # en een / aan het eind. Een doel
 *  buiten de site geeft null. */
export function eigenPad(doel: string): string | null {
  const tekst = doel.trim();
  const eigen = EIGEN_SITE.test(tekst);
  const pad = (eigen ? tekst.replace(EIGEN_SITE, '') : tekst).split(/[?#]/)[0] || (eigen ? '/' : '');
  if (!pad.startsWith('/')) return null;
  return pad.length > 1 ? pad.replace(/\/+$/, '') || '/' : pad;
}

/** De doorverwijzing die de site vindt als een bezoeker op dit doel aankomt, als die er is. */
function vervolg(doel: string, bestaand: Doorverwijzing[]): Doorverwijzing | undefined {
  const pad = eigenPad(doel);
  return pad === null ? undefined : bestaand.find((r) => r.from_path === opzoekPad(pad));
}

/** Waar een bezoeker uiteindelijk uitkomt als hij naar `doel` wordt gestuurd. `lus` betekent dat de
 *  doorverwijzingen in een kring lopen. */
export function einddoel(doel: string, bestaand: Doorverwijzing[]): { doel: string; lus: boolean } {
  const gezien = new Set<string>();
  let nu = doel;
  for (;;) {
    const volgende = vervolg(nu, bestaand);
    if (!volgende) return { doel: nu, lus: false };
    if (gezien.has(volgende.from_path)) return { doel: nu, lus: true };
    gezien.add(volgende.from_path);
    nu = volgende.to_path;
  }
}

const paginaOp = (pad: string, paginas: Pagina[], gepubliceerd: boolean): Pagina | undefined =>
  paginas.find((p) => p.gepubliceerd === gepubliceerd && opzoekPad(p.pad) === opzoekPad(pad));
const noem = (p: Pagina): string => `${p.soort.toLowerCase()} "${p.titel}"`;

export interface Keuring {
  ok: boolean;
  /** Wat het opslaan tegenhoudt, per vak. */
  fouten: Record<string, string>;
  /** Bij een keten: het einddoel waar het doel direct naar kan wijzen. */
  voorstel: string | null;
  /** Houdt het opslaan niet tegen, maar is goed om te weten. */
  letOp: string[];
}

/** Wat er mis is met een nieuw oud adres, of null. */
function keurVan(van: string, bestaand: Doorverwijzing[], paginas: Pagina[]): string | null {
  if (van === '') return 'Vul het oude adres in.';
  if (!van.startsWith('/')) return 'Het oude adres is een pad op de site en begint met /. Vul alleen het deel na greenvision.energy in.';
  if (/\s/.test(van)) return 'In een adres staan geen spaties.';
  if (/[?#]/.test(van)) return 'Zonder ? en zonder #: de site vergelijkt alleen het pad.';
  if (van.includes('//')) return 'In een adres staat nergens een dubbele /.';
  if (van === '/') return 'De homepage bestaat altijd en kan dus niet doorverwijzen.';
  if (van.endsWith('/')) return 'Zonder / aan het eind: de site haalt die er zelf af voordat ze zoekt.';
  if (van !== opzoekPad(van)) return 'Schrijf het oude adres in kleine letters. De site zet hoofdletters eerst om naar kleine letters en zoekt daarna pas.';
  const dubbel = bestaand.find((r) => r.from_path === van);
  if (dubbel) return `Er staat al een doorverwijzing vanaf ${van}, naar ${dubbel.to_path}. Pas die aan in de lijst hieronder.`;
  const pagina = paginaOp(van, paginas, true);
  if (pagina) return `Op ${van} staat een gepubliceerde pagina: ${noem(pagina)}. De site kijkt alleen in de doorverwijzingen als er geen pagina staat, dus deze zou nooit werken.`;
  // Op een vaste pagina kijkt de site niet in de doorverwijzingen: daar ontstaat geen keten. Dat het
  // oude adres dan niets doet, staat als waarschuwing bij het vak (letOpBij).
  const wijstHierheen = VASTE_PAGINAS.includes(van) ? undefined : bestaand.find((r) => vervolgPad(r.to_path) === van);
  if (wijstHierheen) {
    return `De doorverwijzing vanaf ${wijstHierheen.from_path} wijst al naar ${van}. Samen zouden ze een keten vormen. Laat die eerst direct naar het nieuwe doel wijzen.`;
  }
  return null;
}

/** Het pad waarop de site zoekt als een bezoeker naar dit doel wordt gestuurd; null buiten de site. */
function vervolgPad(doel: string): string | null {
  const pad = eigenPad(doel);
  return pad === null ? null : opzoekPad(pad);
}

/** Wat er mis is met een doel, en bij een keten het einddoel als voorstel. */
function keurDoel(van: string, doel: string, bestaand: Doorverwijzing[]): { fout: string | null; voorstel: string | null } {
  const fout = (tekst: string, voorstel: string | null = null) => ({ fout: tekst, voorstel });
  if (doel === '') return fout('Vul het doel in.');
  if (/\s/.test(doel)) return fout('In een adres staan geen spaties.');
  // De database neemt //voorbeeld.nl aan als pad, maar een browser leest het als een andere site.
  if (doel.startsWith('//')) return fout('Een pad begint met één /. Met twee stuurt de browser de bezoeker naar een andere site.');
  if (!doel.startsWith('/') && !(doel.startsWith('https://') && isAdres(doel))) {
    return fout('Het doel is een pad op de site dat met / begint, of een heel adres dat met https:// begint.');
  }
  if (vervolgPad(doel) === van) return fout('Het doel is het oude adres zelf: de bezoeker zou in een kring blijven lopen.');
  const volgende = vervolg(doel, bestaand.filter((r) => r.from_path !== van));
  if (!volgende) return { fout: null, voorstel: null };
  const eind = einddoel(volgende.to_path, bestaand.filter((r) => r.from_path !== van));
  if (eind.lus || vervolgPad(eind.doel) === van) {
    return fout(`Het doel ${volgende.from_path} verwijst zelf door, en komt uiteindelijk weer uit bij ${eind.lus ? 'zichzelf' : 'het oude adres'}. Kies een ander doel.`);
  }
  return fout(`Het doel ${volgende.from_path} verwijst zelf door naar ${eind.doel}. Dat is een keten: wijs direct naar het einddoel.`, eind.doel);
}

const keurStatus = (status: number | string): string | null =>
  ((STATUSSEN as readonly number[]).includes(Number(status)) ? null : 'Kies 301, 302, 307 of 308.');

/** Wat goed is om te weten over een oud adres en een doel, zonder dat het opslaan erop stuit. */
function letOpBij(van: string | null, doel: string | null, paginas: Pagina[]): string[] {
  const uit: string[] = [];
  if (van !== null) {
    if (VASTE_PAGINAS.includes(van)) uit.push(`Volgens de code van de site staat op ${van} een vaste pagina. Zolang die er staat, doet de site niets met deze doorverwijzing.`);
    const concept = paginaOp(van, paginas, false);
    if (concept) uit.push(`Op ${van} staat een concept: ${noem(concept)}. Wordt dat gepubliceerd, dan doet de site niets meer met deze doorverwijzing.`);
  }
  const pad = doel === null ? null : eigenPad(doel);
  if (pad !== null && !VASTE_PAGINAS.includes(opzoekPad(pad)) && !paginaOp(pad, paginas, true)) {
    const concept = paginaOp(pad, paginas, false);
    uit.push(concept
      ? `Op ${pad} staat een concept: ${noem(concept)}. Tot dat gepubliceerd is, komt de bezoeker uit op "niet gevonden".`
      : `De CRM kent geen pagina op ${pad}. Bestaat die niet, dan komt de bezoeker uit op "niet gevonden".`);
  }
  return uit;
}

const klaar = (fouten: Record<string, string>, voorstel: string | null, letOp: string[]): Keuring =>
  ({ ok: Object.keys(fouten).length === 0, fouten, voorstel, letOp: Object.keys(fouten).length === 0 ? letOp : [] });

/** Keurt een nieuwe doorverwijzing tegen wat er al staat en tegen de pagina's van de site. */
export function controleerNieuw(concept: Concept, bestaand: Doorverwijzing[], paginas: Pagina[]): Keuring {
  const van = concept.from_path.trim();
  const doel = concept.to_path.trim();
  const fouten: Record<string, string> = {};
  const foutVan = keurVan(van, bestaand, paginas);
  if (foutVan) fouten.from_path = foutVan;
  const overDoel = keurDoel(van, doel, bestaand);
  if (overDoel.fout) fouten.to_path = overDoel.fout;
  const foutStatus = keurStatus(concept.status);
  if (foutStatus) fouten.status = foutStatus;
  return klaar(fouten, overDoel.voorstel, letOpBij(van, doel, paginas));
}

/** Wat er aan een bestaande doorverwijzing verandert: alleen de kolommen die anders zijn. Het oude
 *  adres zit er nooit bij. */
export function wijziging(vorige: Doorverwijzing, concept: Wijziging): Partial<Pick<Doorverwijzing, 'to_path' | 'status' | 'note'>> {
  const uit: Partial<Pick<Doorverwijzing, 'to_path' | 'status' | 'note'>> = {};
  if (concept.to_path.trim() !== vorige.to_path) uit.to_path = concept.to_path.trim();
  if (Number(concept.status) !== vorige.status) uit.status = Number(concept.status);
  if ((concept.note.trim() || null) !== (vorige.note ?? null)) uit.note = concept.note.trim() || null;
  return uit;
}

/** Keurt een wijziging van een bestaande doorverwijzing. Alleen wat is gewijzigd, wordt nagekeken:
 *  oude inhoud houdt het opslaan van een notitie niet tegen. */
export function controleerWijziging(vorige: Doorverwijzing, concept: Wijziging, bestaand: Doorverwijzing[], paginas: Pagina[]): Keuring {
  const anders = wijziging(vorige, concept);
  const fouten: Record<string, string> = {};
  let voorstel: string | null = null;
  if ('to_path' in anders) {
    const overDoel = keurDoel(vorige.from_path, concept.to_path.trim(), bestaand);
    if (overDoel.fout) fouten.to_path = overDoel.fout;
    voorstel = overDoel.voorstel;
  }
  if ('status' in anders) {
    const foutStatus = keurStatus(concept.status);
    if (foutStatus) fouten.status = foutStatus;
  }
  return klaar(fouten, voorstel, 'to_path' in anders ? letOpBij(null, concept.to_path.trim(), paginas) : []);
}

/** Wat er in de lijst bij een bestaande doorverwijzing staat als de site er niets of iets anders mee
 *  doet dan bedoeld. Leeg = hij werkt. */
export function opmerkingen(rij: Doorverwijzing, bestaand: Doorverwijzing[], paginas: Pagina[]): string[] {
  const uit: string[] = [];
  const hoofdletter = rij.from_path !== opzoekPad(rij.from_path);
  const pagina = hoofdletter ? undefined : paginaOp(rij.from_path, paginas, true);
  if (hoofdletter) uit.push('Werkt niet: in het oude adres staat een hoofdletter. De site zet hoofdletters zelf om naar kleine letters en zoekt daarna pas.');
  if (pagina) uit.push(`Werkt nu niet: op het oude adres staat een gepubliceerde pagina, ${noem(pagina)}.`);
  const zichzelf = vervolgPad(rij.to_path) === rij.from_path;
  const volgende = zichzelf ? undefined : vervolg(rij.to_path, bestaand.filter((r) => r.from_path !== rij.from_path));
  if (zichzelf) uit.push('Wijst naar zichzelf: de bezoeker blijft in een kring lopen.');
  if (volgende) uit.push(`Keten: het doel verwijst zelf door naar ${volgende.to_path}.`);
  // Over het oude adres en het doel valt alleen nog iets te zeggen als het bovenstaande niet speelt.
  uit.push(...letOpBij(hoofdletter || pagina ? null : rij.from_path, zichzelf || volgende ? null : rij.to_path, paginas));
  return uit;
}

/* ── Lezen en schrijven ────────────────────────────────────────────────────── */

function ofFout<T>(antwoord: { data: T | null; error: { message: string } | null }, wat: string): T {
  if (antwoord.error) throw new Error(`${wat}: ${antwoord.error.message}`);
  return antwoord.data as T;
}

export async function haalDoorverwijzingen(client: WebsiteClient): Promise<Doorverwijzing[]> {
  const antwoord = await client.schema('website').from(TABEL).select(KOLOMMEN).order('from_path', { ascending: true });
  return ofFout<Doorverwijzing[]>(antwoord, 'Doorverwijzingen lezen') ?? [];
}

/** De adressen waarop een pagina uit de content staat: elke soort met een eigen pagina per rij. */
export async function haalPaginas(client: WebsiteClient): Promise<Pagina[]> {
  const metPagina = SOORTEN.filter((s) => s.heeftSlug && s.padPrefix);
  const lijsten = await Promise.all(metPagina.map(async (soort) => {
    const antwoord = await client.schema('website').from(soort.tabel).select(`slug, published, ${soort.titelKolom}`);
    const rijen = ofFout<Rij[]>(antwoord, `${soort.meervoud} lezen`) ?? [];
    return rijen.filter((r) => r.slug).map((r): Pagina => ({
      pad: `${soort.padPrefix}${r.slug}`, titel: String(r[soort.titelKolom] ?? ''), soort: soort.enkelvoud, gepubliceerd: r.published === true,
    }));
  }));
  return lijsten.flat();
}

const lees = async (client: WebsiteClient): Promise<{ bestaand: Doorverwijzing[]; paginas: Pagina[] }> => {
  const [bestaand, paginas] = await Promise.all([haalDoorverwijzingen(client), haalPaginas(client)]);
  return { bestaand, paginas };
};

/** Maakt een doorverwijzing aan. Een insert en geen upsert: staat er intussen al een rij met dit oude
 *  adres, dan weigert de database en wordt er niets overschreven. */
export async function maakDoorverwijzing(client: WebsiteClient, concept: Concept): Promise<Doorverwijzing> {
  const { bestaand, paginas } = await lees(client);
  const keuring = controleerNieuw(concept, bestaand, paginas);
  if (!keuring.ok) throw new Error(Object.values(keuring.fouten)[0]);
  const rij = { from_path: concept.from_path.trim(), to_path: concept.to_path.trim(), status: Number(concept.status), note: concept.note.trim() || null };
  const antwoord = await client.schema('website').from(TABEL).insert(rij).select(KOLOMMEN).maybeSingle();
  if (antwoord.error?.code === '23505') throw new Error(`Er staat al een doorverwijzing vanaf ${rij.from_path}. Laad de pagina opnieuw.`);
  const gemaakt = ofFout<Doorverwijzing | null>(antwoord, 'Doorverwijzing aanmaken');
  // RLS weigert zonder fout: geen rij terug betekent dat er niets is aangemaakt.
  if (!gemaakt) throw new Error('De doorverwijzing is niet aangemaakt. Je hebt hier geen schrijfrecht.');
  return gemaakt;
}

export interface Gewijzigd {
  rij: Doorverwijzing;
  /** De kolommen die zijn geschreven. Leeg = er was niets gewijzigd en er is niets geschreven. */
  geschreven: string[];
}

/** Wijzigt het doel, de status of de notitie van een bestaande doorverwijzing. Alleen de gewijzigde
 *  kolommen gaan naar de database; het oude adres nooit. */
export async function wijzigDoorverwijzing(client: WebsiteClient, vorige: Doorverwijzing, concept: Wijziging): Promise<Gewijzigd> {
  const { bestaand, paginas } = await lees(client);
  const nu = bestaand.find((r) => r.from_path === vorige.from_path);
  if (!nu) throw new Error(`De doorverwijzing vanaf ${vorige.from_path} bestaat niet meer. Laad de pagina opnieuw.`);
  const keuring = controleerWijziging(vorige, concept, bestaand, paginas);
  if (!keuring.ok) throw new Error(Object.values(keuring.fouten)[0]);
  const anders = wijziging(vorige, concept);
  const geschreven = Object.keys(anders);
  if (geschreven.length === 0) return { rij: nu, geschreven };
  const antwoord = await client.schema('website').from(TABEL).update(anders).eq('from_path', vorige.from_path).select(KOLOMMEN).maybeSingle();
  const rij = ofFout<Doorverwijzing | null>(antwoord, 'Doorverwijzing opslaan');
  if (!rij) throw new Error('De doorverwijzing is niet opgeslagen. Je hebt hier geen schrijfrecht, of de rij bestaat niet meer.');
  return { rij, geschreven };
}

/** Verwijdert één doorverwijzing: één delete op het oude adres, die de verwijderde rij teruggeeft. Het
 *  oude adres is de sleutel van de tabel, dus er gaat hooguit één rij weg. Komt er geen rij terug, dan
 *  is er niets verwijderd (RLS weigert zonder fout, of de rij was al weg) en is dat een fout. */
export async function verwijderDoorverwijzing(client: WebsiteClient, van: string): Promise<Doorverwijzing> {
  // Zonder oud adres is er niets aan te wijzen: dan gaat er geen delete naar de database.
  if (typeof van !== 'string' || van.trim() === '') throw new Error('Er is geen doorverwijzing gekozen om te verwijderen.');
  const antwoord = await client.schema('website').from(TABEL).delete().eq('from_path', van).select(KOLOMMEN).maybeSingle();
  const rij = ofFout<Doorverwijzing | null>(antwoord, 'Doorverwijzing verwijderen');
  if (!rij) throw new Error(`De doorverwijzing vanaf ${van} is niet verwijderd. Je hebt hier geen schrijfrecht, of ze bestaat niet meer.`);
  return rij;
}
